import React, { useCallback, useEffect } from 'react';
import Styles from './UnifiedLoginSignup.module.scss';
import { ExternalLinkIcon } from '../images/icons/ExternalLinkIcon';
import { ArrowBackIcon } from '../images/icons/ArrowBackIcon';
import { AcceptIcon } from '../images/icons/AcceptIcon';
import { useHistory } from 'react-router';
import { GetTokenSilentlyOptions, useAuth0 } from '@auth0/auth0-react';
import { WarningIcon } from '../images/icons/WarningIcon';
import getEnvVariable from '../utils/environment';
import { ENVIRONMENT } from '../constants';
import {
  EMAIL_ALREADY_EXISTS_ERROR,
  useCreateAccount
} from '../utils/unifiedSignup';
import FullPageLoader from '../components/FullPageLoader';
import { UserIconWithInfo } from '../components/UserIconWithInfo';
import { Button } from '@twilio-paste/core/button';

const bullets = [
  'SMTP and API',
  'Purpose-built mail transfer agent',
  'Globally distributed architecture',
  'Suppression management',
  'Real-Time analytics',
  'Deliverability insights'
];

const auth0Audience = getEnvVariable(ENVIRONMENT.AUTH0_AUDIENCE);
const getTokenParams: GetTokenSilentlyOptions = {
  authorizationParams: {
    audience: auth0Audience
  }
};

const SG_WEBSITE_URL = getEnvVariable(ENVIRONMENT.SG_WEBSITE_URL);
const LOGIN_HOST_URL = getEnvVariable(ENVIRONMENT.LOGIN_HOST_URL);

export const UnifiedLoginSignup = () => {
  const { loginWithRedirect, getAccessTokenSilently, isAuthenticated, user } =
    useAuth0();
  const { createAccount, isError, isLoading, errorMsg } = useCreateAccount();
  const history = useHistory();
  useEffect(() => {
    const getToken = async () => {
      if (!isAuthenticated) {
        loginWithRedirect({
          appState: { targetUrl: '/unified_login/signup' }
        });
      }
    };
    getToken();
  }, [isAuthenticated, loginWithRedirect]);

  const handleCreateAccount = useCallback(() => {
    const getTokenAndCreate = async () => {
      const { id_token, access_token } = await getAccessTokenSilently({
        ...getTokenParams,
        detailedResponse: true
      });
      const success = await createAccount(access_token, id_token);
      if (success) {
        history.replace('/unified_login/account_details');
      }
    };
    getTokenAndCreate();
  }, [createAccount, getAccessTokenSilently]);

  const goToMpSignup = useCallback((event) => {
    event.preventDefault();
    history.push('/');
  }, []);

  const clickHereToLogin = (
    <a
      onClick={(e) => {
        e.preventDefault();
        window.location.replace(`${LOGIN_HOST_URL}/login/identifier`);
      }}
    >
      {'here'}
    </a>
  );

  return isAuthenticated ? (
    <main className={'paste'}>
      {isError && (
        <div className={Styles['errorBanner']}>
          <WarningIcon color={'#E36919'} size={'20px'} />
          <div>
            <strong>Unable to create account.</strong> {errorMsg}{' '}
            {errorMsg === EMAIL_ALREADY_EXISTS_ERROR ? (
              <>Please login {clickHereToLogin}.</>
            ) : null}
          </div>
        </div>
      )}
      <article style={{ padding: '35px' }}>
        <section className={Styles['unified_signup_container']}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              marginTop: '60px'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxWidth: '650px'
              }}
            >
              <h1
                style={{ textAlign: 'center' }}
                className={Styles['signup_title']}
              >
                {'Use SendGrid with your Twilio credentials'}
              </h1>
              <p className={Styles['signup_description']}>
                {
                  'Get started with our proven email infrastructure by using your Twilio credentials to access SendGrid.'
                }
              </p>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center'
                }}
              >
                <a href={`${SG_WEBSITE_URL}/why-sendgrid`} target="_blank">
                  {'Learn more about SendGrid'}
                </a>
                <ExternalLinkIcon color={'#0263E0'} />
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              marginTop: '40px',
              columnGap: '25px',
              rowGap: '25px',
              flexWrap: 'wrap'
            }}
          >
            <div className={Styles['card']}>
              <UserIconWithInfo
                givenName={user?.given_name ?? ''}
                familyName={user?.family_name ?? ''}
                email={user?.email ?? ''}
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignContent: 'center',
                  rowGap: '16px'
                }}
              >
                <Button
                  variant={'primary'}
                  onClick={handleCreateAccount}
                  loading={isLoading}
                  data-testid="continueWithTwilioCredentials"
                  name="Continue with my Twilio credentials"
                >
                  Continue with my Twilio credentials
                </Button>
                <h5 style={{ textAlign: 'center' }}>
                  <a href="/" onClick={goToMpSignup}>
                    {'Signup with a different email'}
                  </a>
                </h5>
              </div>
              <h6 style={{ textAlign: 'center' }}>
                By continuing, you agree to the{' '}
                <a href={`${SG_WEBSITE_URL}/policies/tos`} target="_blank">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href={`${SG_WEBSITE_URL}/policies/privacy/services-privacy-policy`}
                  target="_blank"
                >
                  Privacy Notice
                </a>
                .
              </h6>
            </div>

            <div
              style={{
                justifyContent: 'center',
                alignItems: 'flex-start',
                backgroundColor: '#F9FAFC'
              }}
              className={`${Styles['card']}`}
            >
              <h5 style={{ marginBottom: '24px' }}>
                Your free SendGrid plan includes:
              </h5>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '10px'
                }}
              >
                {bullets.map((value, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      columnGap: '8px'
                    }}
                  >
                    <AcceptIcon size={'20px'} color={'#52BD94'} />
                    {value}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </article>
    </main>
  ) : (
    <FullPageLoader />
  );
};
